/**
 * Signal glitch effect with random appearing katakana characters
 */
export class MatrixGlitch {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fontSize: number = 14;
  private chars: string =
    "ｦｧｨｩｪｫｬｭｮｯｰｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ";
  private animationId: number = 0;
  private glitches: Array<{
    x: number;
    y: number;
    text: string;
    life: number;
    maxLife: number;
    color: string;
  }> = [];

  constructor(canvasId: string) {
    this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!this.canvas) {
      throw new Error(`Canvas element with id "${canvasId}" not found`);
    }

    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context");
    }
    this.ctx = ctx;

    this.setupCanvas();
    this.animate();

    // Handle window resize
    window.addEventListener("resize", () => this.setupCanvas());
  }

  private setupCanvas(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  private createGlitch(): void {
    // Random number of characters (3-20) to create words/fragments
    const length = Math.floor(Math.random() * 18) + 3;
    let text = "";
    for (let i = 0; i < length; i++) {
      text += this.chars[Math.floor(Math.random() * this.chars.length)];
    }

    // Random bright blue shades
    const blues = [
      "#00d4ff",
      "#0095ff",
      "#00b8ff",
      "#00e1ff",
      "#33e0ff",
      "#00c3ff",
    ];
    const color = blues[Math.floor(Math.random() * blues.length)];

    this.glitches.push({
      x: Math.random() * this.canvas.width,
      y: Math.random() * this.canvas.height,
      text,
      life: 0,
      maxLife: Math.floor(Math.random() * 20) + 10, // 10-30 frames
      color,
    });
  }

  private draw = (): void => {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Create new glitches frequently
    if (Math.random() > 0.9) {
      // 70% chance each frame = many per second
      this.createGlitch();
    }

    // Draw and update existing glitches
    for (let i = this.glitches.length - 1; i >= 0; i--) {
      const glitch = this.glitches[i];

      // Calculate opacity (fade in and out)
      const progress = glitch.life / glitch.maxLife;
      let opacity;
      if (progress < 0.2) {
        opacity = progress / 0.2; // fade in
      } else if (progress > 0.7) {
        opacity = (1 - progress) / 0.3; // fade out
      } else {
        opacity = 1; // full visibility
      }

      // Draw text without shadows
      this.ctx.font = `300 ${this.fontSize}px "JetBrains Mono", monospace`;
      this.ctx.textBaseline = "middle";

      // Convert hex to rgba with opacity
      const hex = glitch.color.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;

      this.ctx.fillText(glitch.text, glitch.x, glitch.y);

      // Update life
      glitch.life++;

      // Remove if expired
      if (glitch.life >= glitch.maxLife) {
        this.glitches.splice(i, 1);
      }
    }
  };

  private animate = (): void => {
    this.draw();
    this.animationId = requestAnimationFrame(this.animate);
  };

  public destroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    window.removeEventListener("resize", this.setupCanvas);
  }
}
